// Global active-strategy state.
//
// Whichever strategy is "loaded" — via template, AI generation, manual block
// authoring, or restoration from a saved variant — lives here. Every page
// that needs the current strategy (Strategy Builder, Backtest, AI Optimize,
// Replay, Deploy, Signals) reads from this single source instead of holding
// its own copy.
//
// Persistence: serialised to localStorage on every change with a short debounce.
// Hydrates on mount. Cross-tab sync via the `storage` event so opening a
// second tab inherits the active strategy.
//
// Future: a Supabase-backed `current_strategy` row keyed on user_id would
// let the active strategy follow the user across devices. localStorage is
// good enough for MVP (single-device session continuity).

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Block } from '../types';
import { BlockType } from '../types';
import { supabase } from '../services/supabaseClient';
import { captureOriginalSizing } from '../services/originalSizing';
import { healCuratedLibraryBlocks } from '../services/customBlockLibraryService';
import { healVestigialLegacyCode } from '../engine/migration/healVestigialCode';
// P10.1 hotfix — rewire saved structural strategies off the defective entry bridge
import { healStructuralEntryBridge } from '../engine/migration/healStructuralEntryBridge';
import { healStructuralExitBridges } from '../engine/migration/healStructuralExitBridges';
import { peekEvidenceImport, clearEvidenceImport, EVIDENCE_IMPORT_EVENT } from '../utils/evidenceImport';
// P9.2B — PRODUCT-level capability-pack registration: the methodology-neutral core runs
// without packs, but the shipped product loads both so pack-expressed structural
// strategies (price-structure / calendar concepts) execute instead of failing closed.
import { registerPack } from '../engine/setup/packs/registry';
import { PRICE_STRUCTURE_PACK } from '../engine/setup/packs/priceStructurePack';
import { CALENDAR_PACK } from '../engine/setup/packs/calendarPack';
registerPack(PRICE_STRUCTURE_PACK);
registerPack(CALENDAR_PACK);

export type StrategySource = 'template' | 'ai' | 'manual' | 'saved' | 'imported' | 'evidence-import' | null;

export interface StrategyState {
  blocks: Block[];
  strategyName: string | null;
  strategySource: StrategySource;
  // Wall-clock at the moment the strategy was loaded (or last meaningfully
  // updated). Used for "5m ago" hints in the header.
  loadedAt: number | null;
  // Optional opaque metadata — e.g. the template id, AI _meta payload, etc.
  meta?: Record<string, unknown>;
}

export type CloudSyncStatus = 'idle' | 'syncing' | 'synced' | 'offline' | 'unauthenticated';

export interface StrategyContextValue extends StrategyState {
  // Replace the entire strategy. Use this when loading a template, finishing
  // AI generation, or restoring a saved variant.
  setStrategy: (
    blocks: Block[],
    name: string | null,
    source: StrategySource,
    meta?: Record<string, unknown>,
  ) => void;
  // Patch just the blocks (e.g. user added/removed/reordered blocks on the
  // canvas). Preserves name + source + meta.
  patchBlocks: (blocks: Block[]) => void;
  // S86 — patch the opaque `meta` (e.g. persist the source template id so
  // "Update Template" survives navigation) WITHOUT bumping loadedAt or
  // touching blocks/name/source, so the Builder doesn't treat it as a fresh
  // load (no canvas re-arrange).
  patchMeta: (patch: Record<string, unknown>) => void;
  // Wipe the active strategy back to empty.
  clearStrategy: () => void;
  // Mark the current active strategy as "saved" — callers invoke this
  // after a successful Save Run so a subsequent isDirty check correctly
  // reports the work as no longer dirty. Doesn't change blocks/name/meta,
  // doesn't bump loadedAt (preserves auto-arrange refs in the Builder).
  markCurrentAsSaved: () => void;
  // Rename the active strategy (e.g. after a successful named Save). Patches
  // ONLY strategyName — no block/source/meta change, no loadedAt bump — so
  // the header updates without the Builder treating it as a fresh load
  // (which would re-trigger auto-arrange). No-op on an empty/blank name or
  // when there's no strategy.
  renameCurrent: (name: string) => void;
  // Convenience selector.
  hasStrategy: boolean;
  // True when the active strategy has unsaved work that would be lost on
  // replace. Two cases:
  //   • source === 'manual'  → user edited a previously-loaded strategy
  //   • source === null with non-empty blocks → user dragged from scratch
  // The other sources (template/ai/saved/imported with no edits since)
  // are re-loadable artifacts and don't trigger the guard.
  isDirty: boolean;
  // Cross-device sync surface — for the header badge.
  cloudSync: {
    status: CloudSyncStatus;
    lastSyncedAt: number | null;
  };
}

const EMPTY_STATE: StrategyState = {
  blocks: [],
  strategyName: null,
  strategySource: null,
  loadedAt: null,
  meta: undefined,
};

const StrategyContext = createContext<StrategyContextValue>({
  ...EMPTY_STATE,
  setStrategy: () => {},
  patchBlocks: () => {},
  patchMeta: () => {},
  clearStrategy: () => {},
  markCurrentAsSaved: () => {},
  renameCurrent: () => {},
  hasStrategy: false,
  isDirty: false,
  cloudSync: { status: 'idle', lastSyncedAt: null },
});

const STORAGE_KEY = 'tradeatory.strategy.current';
const DEBOUNCE_LOCAL_MS = 400; // localStorage write — fast, on idle
const DEBOUNCE_CLOUD_MS = 1500; // Supabase write — slower, on bigger idle

// ─── Stale-fitness label sanitiser ──────────────────────────────────────────
// User reported a strategy named "BTC 1m Fitness: 0.57" whose backtest
// on the same period showed net -$88.96 / Sharpe -3.89. The "Fitness: 0.57"
// fragment was a self-attached note from a prior optimization run — but
// fitness is a walk-forward composite over a HISTORICAL window, while the
// backtest is a flat single-pass over a (different) period. Keeping the
// stale fitness in the strategy name caused the user to read it as a live
// claim. Strip it from the displayed name and stash the extracted value in
// meta.staleFitnessNote so the UI can render it as a (was-Fitness: X) chip
// instead — informative but not misleading.
const STALE_FITNESS_RE = /\s*[—–-]?\s*Fitness:\s*-?\d+(?:\.\d+)?\s*/gi;
export function extractStaleFitness(name: string | null | undefined): {
  cleanName: string | null;
  staleFitness: number | null;
} {
  if (!name) return { cleanName: null, staleFitness: null };
  // Pull out the first Fitness: X occurrence's numeric value.
  const m = name.match(/Fitness:\s*(-?\d+(?:\.\d+)?)/i);
  const staleFitness = m ? Number(m[1]) : null;
  // Strip ALL Fitness: X fragments (and surrounding em-dash/hyphen
  // separators) so a name like "BTC 1m — Fitness: 0.57" cleanly becomes
  // "BTC 1m". Collapse double spaces and trim.
  const stripped = name.replace(STALE_FITNESS_RE, ' ').replace(/\s{2,}/g, ' ').trim();
  // If stripping leaves an empty string, return null (caller falls back
  // to the strategy_config title).
  const cleanName = stripped.length > 0 ? stripped : null;
  return { cleanName, staleFitness: Number.isFinite(staleFitness as number) ? staleFitness : null };
}

// Convenience wrapper: take a partial StrategyState shape, return the same
// shape with strategyName sanitised and the staleFitness merged into meta.
function sanitiseStateName(state: Partial<StrategyState>): Partial<StrategyState> {
  if (!state.strategyName) return state;
  const { cleanName, staleFitness } = extractStaleFitness(state.strategyName);
  if (cleanName === state.strategyName) return state; // no change
  const meta = { ...(state.meta ?? {}) } as Record<string, unknown>;
  if (staleFitness != null) meta.staleFitnessNote = staleFitness;
  return { ...state, strategyName: cleanName, meta };
}

// ─── Storage helpers ────────────────────────────────────────────────────────

function loadFromStorage(): StrategyState {
  if (typeof window === 'undefined') return EMPTY_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw);
    // Defensive shape check — corrupted blob shouldn't crash the app.
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.blocks)) {
      return EMPTY_STATE;
    }
    const hydrated: StrategyState = {
      blocks:         healStructuralExitBridges(healStructuralEntryBridge(healVestigialLegacyCode(healCuratedLibraryBlocks(healConfigSizing(parsed.blocks))))),
      strategyName:   typeof parsed.strategyName === 'string' ? parsed.strategyName : null,
      strategySource: parsed.strategySource ?? null,
      loadedAt:       typeof parsed.loadedAt === 'number' ? parsed.loadedAt : null,
      meta:           parsed.meta ?? undefined,
    };
    // Strip any stale "Fitness: X" fragment off the strategy name. See
    // sanitiseStateName above for why — the fragment is a frozen historical
    // claim that misleads the user when displayed alongside fresh metrics.
    return { ...hydrated, ...sanitiseStateName(hydrated) } as StrategyState;
  } catch (e) {
    console.warn('[StrategyContext] localStorage hydrate failed:', e);
    return EMPTY_STATE;
  }
}

function persistToStorage(state: StrategyState): void {
  if (typeof window === 'undefined') return;
  try {
    if (state.blocks.length === 0 && !state.strategyName) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  } catch (e) {
    // Quota exceeded, browser private mode, etc. Non-fatal.
    console.warn('[StrategyContext] localStorage persist failed:', e);
  }
}

// ─── Supabase cross-device sync ────────────────────────────────────────────
//
// One row per user in user_preferences.current_strategy (jsonb). Most-recent
// loadedAt wins on conflict. Failures (offline, RLS, etc.) downgrade silently
// to localStorage-only mode — the user can keep working.

async function loadFromCloud(): Promise<StrategyState | null> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return null;
  const { data, error } = await supabase
    .from('user_preferences')
    .select('current_strategy')
    .eq('user_id', userData.user.id)
    .maybeSingle();
  if (error) {
    console.warn('[StrategyContext] cloud load failed:', error.message);
    return null;
  }
  const payload = data?.current_strategy as Partial<StrategyState> | null | undefined;
  if (!payload || !Array.isArray(payload.blocks)) return null;
  const hydrated: StrategyState = {
    blocks:         payload.blocks,
    strategyName:   typeof payload.strategyName === 'string' ? payload.strategyName : null,
    strategySource: payload.strategySource ?? null,
    loadedAt:       typeof payload.loadedAt === 'number' ? payload.loadedAt : null,
    meta:           payload.meta ?? undefined,
  };
  // Same stale-fitness strip as the localStorage hydrate path.
  return { ...hydrated, ...sanitiseStateName(hydrated) } as StrategyState;
}

async function persistToCloud(state: StrategyState): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return;
  const payload = state.blocks.length === 0 && !state.strategyName ? null : state;
  // Upsert by user_id — user_preferences may or may not have a row yet for
  // this user. ON CONFLICT (user_id) replaces current_strategy only.
  const { error } = await supabase
    .from('user_preferences')
    .upsert(
      { user_id: userData.user.id, current_strategy: payload },
      { onConflict: 'user_id' },
    );
  if (error) {
    console.warn('[StrategyContext] cloud persist failed:', error.message);
    throw error;
  }
}

// ─── Strategy-name fallback ─────────────────────────────────────────────────
// When a strategy is loaded without an explicit name (template apply, AI
// generation, import, restore), fall back to the STRATEGY_CONFIG block's
// title so the header shows the real name instead of "Unnamed strategy".
// Mirrors the engine's readConfig precedence: data.title, then data.name.
function strategyConfigTitle(blocks: Block[]): string | null {
  const cfg = blocks.find((b) => b.type === BlockType.STRATEGY_CONFIG)?.data as
    | Record<string, unknown>
    | undefined;
  if (!cfg) return null;
  const t = cfg.title;
  if (typeof t === 'string' && t.trim()) return t.trim();
  const n = cfg.name;
  if (typeof n === 'string' && n.trim()) return n.trim();
  return null;
}

// ─── Loaded-strategy self-heal: position sizing ─────────────────────────────
// Mirrors generate-strategy's enrichBlocks (server side): a STRATEGY_CONFIG
// block with no default_qty_type / no positive default_qty_value trips the
// Builder's sizing guard and shows the strategy as "Invalid" even though the
// engine still trades it safely on the %-equity default. Strategies created
// BEFORE the server fix (or imported / hand-edited externally) only live in
// localStorage / the cloud row, so the server fix can't reach them — we heal
// them here, at every point a strategy ENTERS the Builder state, with the
// exact same explicit safe default the engine uses (percent_equity @ 10).
//
// This does NOT mask the guard for the hand-build flow: a config block added
// from the palette already ships with valid sizing (BlockCanvas defaults), so
// the guard only ever fired for externally-sourced strategies — exactly the
// case this rescues. Pure: returns the SAME array ref when nothing changed so
// it never forces a spurious re-render.
function healConfigSizing(blocks: Block[]): Block[] {
  if (!Array.isArray(blocks)) return blocks;
  const idx = blocks.findIndex((b) => b.type === BlockType.STRATEGY_CONFIG);
  if (idx < 0) return blocks;
  const d = (blocks[idx]?.data ?? {}) as Record<string, unknown>;
  const methodMissing =
    typeof d.default_qty_type !== 'string' || (d.default_qty_type as string).trim() === '';
  const valueMissing = !(Number(d.default_qty_value) > 0);
  if (!methodMissing && !valueMissing) return blocks;
  const next = blocks.slice();
  next[idx] = {
    ...blocks[idx],
    data: {
      ...d,
      ...(methodMissing ? { default_qty_type: 'percent_equity' } : {}),
      ...(valueMissing ? { default_qty_value: 10 } : {}),
    },
  } as Block;
  return next;
}

// Loaded-strategy self-heal for vestigial retired `data.code` lives in
// engine/migration/healVestigialCode.ts (pure; regression-tested in
// scripts/test-vestigial-code-heal.mjs). Applied at every point a strategy ENTERS the
// Builder, beside healConfigSizing / healCuratedLibraryBlocks.

// ─── Provider ───────────────────────────────────────────────────────────────

export const StrategyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<StrategyState>(loadFromStorage);
  const [syncStatus, setSyncStatus] = useState<CloudSyncStatus>('idle');
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const localTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cloudTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cloudHydrated = useRef<boolean>(false);

  // ─── localStorage persist (fast path) ────────────────────────────────────
  useEffect(() => {
    if (localTimer.current) clearTimeout(localTimer.current);
    localTimer.current = setTimeout(() => persistToStorage(state), DEBOUNCE_LOCAL_MS);
    return () => {
      if (localTimer.current) clearTimeout(localTimer.current);
    };
  }, [state]);

  // ─── Original-sizing baseline capture ────────────────────────────────────
  // Snapshot each block's sizing the first time we see a freshly-loaded
  // strategy (no-clobber per loadedAt), so "Restore original sizing" in the
  // Builder AND Backtest can always recover the as-loaded values regardless of
  // which window the user edits in. Transient (in-memory) — never a money-math
  // source; the engine/converter always read the live blocks.
  useEffect(() => {
    captureOriginalSizing(state.loadedAt ?? null, state.blocks);
  }, [state.loadedAt, state.blocks]);

  // ─── Evidence-page import (signup funnel) ────────────────────────────────
  // A visitor clicked "Open in Builder" on a published Evidence candidate. The
  // frozen block graph waits in localStorage until an AUTHENTICATED session
  // exists, then loads onto the canvas exactly once (an explicit user action —
  // it intentionally replaces the current canvas, like loading any strategy).
  // Provenance rides in meta.evidenceImport for the presentational banner.
  const tryApplyEvidenceImport = useCallback(async () => {
    const pending = peekEvidenceImport();
    if (!pending) return;
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) return; // stays stashed until sign-up/sign-in completes
    clearEvidenceImport();
    setState({
      blocks: healStructuralExitBridges(healStructuralEntryBridge(healVestigialLegacyCode(healCuratedLibraryBlocks(healConfigSizing(pending.blocks))))),
      strategyName: pending.name,
      strategySource: 'evidence-import',
      loadedAt: Date.now(),
      meta: { evidenceImport: { source: pending.source, id: pending.id, verdict: pending.verdict, provenance: pending.provenance } },
    });
  }, []);
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') void tryApplyEvidenceImport();
    });
    // Already-signed-in sessions: auth events won't refire mid-session, so the Evidence
    // page announces a fresh stash and we apply it immediately (same click, same tab).
    const onStash = () => { void tryApplyEvidenceImport(); };
    window.addEventListener(EVIDENCE_IMPORT_EVENT, onStash);
    return () => { sub.subscription.unsubscribe(); window.removeEventListener(EVIDENCE_IMPORT_EVENT, onStash); };
  }, [tryApplyEvidenceImport]);

  // ─── Supabase initial hydrate (cross-device) ─────────────────────────────
  // On mount, async-fetch the cloud row. If its loadedAt is NEWER than the
  // locally-hydrated state, replace local with cloud (the user updated on
  // a different device). If equal/older, keep local — it's the source of
  // truth for THIS session and will sync up to the cloud shortly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setSyncStatus('syncing');
        const cloud = await loadFromCloud();
        if (cancelled) return;
        if (cloud) {
          const cloudTs = cloud.loadedAt ?? 0;
          const localTs = state.loadedAt ?? 0;
          if (cloudTs > localTs) {
            // Cloud is newer — different device wrote later. Pull it in
            // (self-heal sizing on the way in, same as local/setStrategy).
            setState({ ...cloud, blocks: healStructuralExitBridges(healStructuralEntryBridge(healVestigialLegacyCode(healCuratedLibraryBlocks(healConfigSizing(cloud.blocks))))) });
          }
          setLastSyncedAt(Date.now());
          setSyncStatus('synced');
        } else {
          // No cloud row OR not signed in.
          const { data: userData } = await supabase.auth.getUser();
          setSyncStatus(userData.user ? 'synced' : 'unauthenticated');
        }
        cloudHydrated.current = true;
      } catch {
        if (!cancelled) setSyncStatus('offline');
        cloudHydrated.current = true;
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Supabase persist (slow path, longer debounce) ───────────────────────
  // Only writes AFTER initial cloud hydration completed — so we don't
  // accidentally overwrite a newer cloud version with stale local data
  // during the boot race.
  useEffect(() => {
    if (!cloudHydrated.current) return;
    if (cloudTimer.current) clearTimeout(cloudTimer.current);
    cloudTimer.current = setTimeout(async () => {
      setSyncStatus('syncing');
      try {
        await persistToCloud(state);
        setLastSyncedAt(Date.now());
        setSyncStatus('synced');
      } catch {
        setSyncStatus('offline');
      }
    }, DEBOUNCE_CLOUD_MS);
    return () => {
      if (cloudTimer.current) clearTimeout(cloudTimer.current);
    };
  }, [state]);

  // ─── Cross-tab sync via storage event (same-device, different tabs) ──────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setState(loadFromStorage());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ─── beforeunload / pagehide flush ───────────────────────────────────────
  // Closes the F5-during-debounce risk window.
  //   • localStorage write is synchronous, completes during unload — protects
  //     against same-device reload data loss.
  //   • Cloud write uses fetch+keepalive, best-effort, survives page tear-down
  //     but doesn't block navigation. PostgREST direct (bypasses supabase-js
  //     which uses regular fetch).
  // Captures user id + token via a ref so the handler runs synchronously
  // without an async auth round-trip during unload.
  const authRef = useRef<{ userId: string | null; token: string | null }>({ userId: null, token: null });
  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      const { data: s } = await supabase.auth.getSession();
      authRef.current = { userId: u.user?.id ?? null, token: s.session?.access_token ?? null };
    })();
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      authRef.current = { userId: session?.user?.id ?? null, token: session?.access_token ?? null };
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const flush = () => {
      // 1. Sync localStorage write — fast, runs before unload completes.
      persistToStorage(state);
      // 2. Best-effort cloud upsert with keepalive.
      const { userId, token } = authRef.current;
      if (!userId || !token) return;
      const payload = state.blocks.length === 0 && !state.strategyName ? null : state;
      try {
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_preferences?on_conflict=user_id`, {
          method: 'POST',
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Prefer: 'resolution=merge-duplicates',
          },
          body: JSON.stringify({ user_id: userId, current_strategy: payload }),
          keepalive: true,
        });
      } catch {
        // Best-effort; failures are silent. localStorage already has the data.
      }
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide',     flush); // iOS Safari
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide',     flush);
    };
  }, [state]);

  const setStrategy = useCallback<StrategyContextValue['setStrategy']>((blocks, name, source, meta) => {
    const healed = healStructuralExitBridges(healStructuralEntryBridge(healVestigialLegacyCode(healCuratedLibraryBlocks(healConfigSizing(blocks)))));
    const rawName =
      typeof name === 'string' && name.trim()
        ? name.trim()
        : strategyConfigTitle(healed);
    // Strip stale "Fitness: X" fragments — see sanitiseStateName above.
    // The extracted value is preserved on meta.staleFitnessNote so the
    // UI can still show "(was Fitness: 0.57)" as a chip.
    const { cleanName, staleFitness } = extractStaleFitness(rawName);
    const mergedMeta: Record<string, unknown> = { ...(meta ?? {}) };
    if (staleFitness != null) mergedMeta.staleFitnessNote = staleFitness;
    setState({
      blocks: healed,
      strategyName: cleanName ?? rawName, // if strip leaves empty, keep original
      strategySource: source,
      loadedAt: Date.now(),
      meta: Object.keys(mergedMeta).length > 0 ? mergedMeta : meta,
    });
  }, []);

  const patchBlocks = useCallback<StrategyContextValue['patchBlocks']>((blocks) => {
    setState((prev) => ({
      ...prev,
      blocks,
      // Mark manual edits as 'manual' once the user actually mutates a
      // template/AI strategy on the canvas. Keeps provenance honest.
      strategySource: prev.strategySource === 'template' || prev.strategySource === 'ai' || prev.strategySource === 'saved' || prev.strategySource === 'imported'
        ? 'manual'
        : prev.strategySource,
      loadedAt: Date.now(),
    }));
  }, []);

  // S86 — merge a patch into the opaque `meta` without bumping loadedAt or
  // touching blocks/name/source (same discipline as markCurrentAsSaved), so
  // the Builder's auto-arrange-on-load ref stays intact. Used to persist the
  // source template id (meta.templateId) so the "Update Template" save path
  // survives a Builder remount (navigating away and back).
  const patchMeta = useCallback<StrategyContextValue['patchMeta']>((patch) => {
    setState((prev) => prev.blocks.length === 0
      ? prev
      : { ...prev, meta: { ...((prev.meta as Record<string, unknown> | undefined) ?? {}), ...patch } });
  }, []);

  const clearStrategy = useCallback(() => {
    setState(EMPTY_STATE);
  }, []);

  // Mark the current strategy as saved (e.g. after Save Run). Doesn't bump
  // loadedAt — keeps the Builder's auto-arrange-on-load ref intact so the
  // canvas doesn't re-arrange just because we updated provenance.
  const markCurrentAsSaved = useCallback(() => {
    setState((prev) => prev.blocks.length === 0
      ? prev
      : { ...prev, strategySource: 'saved' });
  }, []);

  // Rename only — same no-loadedAt-bump discipline as markCurrentAsSaved so
  // the canvas doesn't re-arrange just because the name changed. Keeps the
  // same `blocks` reference, so the Builder's block-sync effect is a no-op.
  const renameCurrent = useCallback<StrategyContextValue['renameCurrent']>((name) => {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    if (!trimmed) return;
    // Strip stale fitness so the user can't accidentally re-attach a
    // misleading label by typing "BTC 1m Fitness: 0.57" into the rename
    // dialog. The extracted value is preserved in meta.staleFitnessNote.
    const { cleanName, staleFitness } = extractStaleFitness(trimmed);
    const finalName = cleanName ?? trimmed;
    setState((prev) => {
      if (prev.blocks.length === 0) return prev;
      if (prev.strategyName === finalName && (staleFitness == null || (prev.meta as Record<string, unknown> | undefined)?.staleFitnessNote === staleFitness)) {
        return prev;
      }
      const nextMeta: Record<string, unknown> = { ...((prev.meta ?? {}) as Record<string, unknown>) };
      if (staleFitness != null) nextMeta.staleFitnessNote = staleFitness;
      return { ...prev, strategyName: finalName, meta: nextMeta };
    });
  }, []);

  const isDirty = state.blocks.length > 0
    && (state.strategySource === 'manual' || state.strategySource === null);

  const value = useMemo<StrategyContextValue>(() => ({
    ...state,
    setStrategy,
    patchBlocks,
    patchMeta,
    clearStrategy,
    markCurrentAsSaved,
    renameCurrent,
    hasStrategy: state.blocks.length > 0,
    isDirty,
    cloudSync: { status: syncStatus, lastSyncedAt },
  }), [state, setStrategy, patchBlocks, patchMeta, clearStrategy, markCurrentAsSaved, renameCurrent, isDirty, syncStatus, lastSyncedAt]);

  return (
    <StrategyContext.Provider value={value}>
      {children}
    </StrategyContext.Provider>
  );
};

export function useStrategy(): StrategyContextValue {
  return useContext(StrategyContext);
}
